/**
 * Converts between ADF (Atlassian Document Format) and Y.js XmlFragment for
 * collaborative rich text storage. Used by {@link YTextValue} to persist rich
 * text alongside plain text in Y.Text.
 *
 * Block nodes (paragraph, heading, listItem, etc.) map to XmlElements; inline
 * content (text with marks, emoji, links, mentions) maps to XmlText or nested
 * XmlElements. The structure preserves ADF semantics for roundtrip fidelity.
 */
import * as Y from 'yjs';

import { type ADFDocument } from '@canvas/adf-types';

const BLOCK_NODE_TYPE_NAMES = [
  'paragraph',
  'heading',
  'blockquote',
  'codeBlock',
  'bulletList',
  'orderedList',
  'listItem',
  'panel',
  'rule',
  'table',
  'tableRow',
  'tableCell',
  'tableHeader'
] as const;

export type AdfBlockNodeType = (typeof BLOCK_NODE_TYPE_NAMES)[number];

const BLOCK_NODE_TYPES = new Set<string>(BLOCK_NODE_TYPE_NAMES);

/**
 * A block-level ADF node (paragraph, heading, list, etc.) as a record.
 * Used by {@link isBlockNode} for type narrowing.
 */
export interface AdfBlockNodeRecord {
  type: AdfBlockNodeType;
  attrs?: Record<string, unknown>;
  content?: unknown[];
  [key: string]: unknown;
}

/**
 * Type guard for block-level ADF nodes. Returns true when the node's `type`
 * is one of the known block types (paragraph, heading, bulletList, etc.).
 */
export function isBlockNode(node: Record<string, unknown>): node is AdfBlockNodeRecord {
  const type = node?.type;
  return typeof type === 'string' && BLOCK_NODE_TYPES.has(type);
}

/**
 * Writes an ADF document into a Y.XmlFragment, replacing any existing content.
 * Block nodes become XmlElements; inline content (text, marks, emoji, links,
 * mentions) becomes XmlText or nested XmlElements.
 */
export function writeAdfToXmlFragment(adf: ADFDocument, fragment: Y.XmlFragment): void {
  fragment.delete(0, fragment.length);
  if (!adf.content) {
    return;
  }
  for (const blockNode of adf.content) {
    fragment.push([adfBlockNodeToXmlElement(blockNode)]);
  }
}

/**
 * Converts a Y.XmlFragment back to an ADF document. Returns undefined when
 * the fragment is empty. XmlElements map to block or inline nodes; XmlText
 * maps to text nodes with optional marks.
 */
export function xmlFragmentToAdf(fragment: Y.XmlFragment): ADFDocument | undefined {
  if (fragment.length === 0) {
    return undefined;
  }
  const content: Record<string, any>[] = [];
  for (let i = 0; i < fragment.length; i++) {
    const child = fragment.get(i);
    if (child instanceof Y.XmlElement) {
      content.push(xmlElementToAdfNode(child));
    }
  }
  return { type: 'doc', version: 1, content } as ADFDocument;
}

function adfInlineContentToXmlChildren(
  content: Record<string, any>[]
): Y.XmlText[] | Y.XmlElement[] {
  const children: (Y.XmlText | Y.XmlElement)[] = [];
  for (const node of content) {
    if (node.type === 'text' && node.text) {
      const markAttrs: Record<string, Record<string, unknown>> = {};
      if (Array.isArray(node.marks) && node.marks.length > 0) {
        for (const mark of node.marks) {
          markAttrs[mark.type] = mark.attrs ?? {};
        }
      }
      const yt = new Y.XmlText();
      yt.insert(0, node.text, Object.keys(markAttrs).length > 0 ? markAttrs : undefined);
      children.push(yt);
    } else if (node.type === 'emoji') {
      const el = new Y.XmlElement('emoji');
      if (node.attrs) {
        for (const [key, value] of Object.entries(
          node.attrs as Record<string, unknown>
        )) {
          if (value !== null && value !== undefined) {
            el.setAttribute(key, JSON.stringify(value));
          }
        }
      }
      children.push(el);
    } else if (node.type === 'link') {
      const el = new Y.XmlElement('link');
      if (node.attrs) {
        for (const [key, value] of Object.entries(
          node.attrs as Record<string, unknown>
        )) {
          if (value !== null && value !== undefined) {
            el.setAttribute(key, JSON.stringify(value));
          }
        }
      }
      if (node.content?.length) {
        const linkChildren = adfInlineContentToXmlChildren(node.content);
        for (const c of linkChildren) {
          el.push([c]);
        }
      }
      children.push(el);
    } else if (node.type === 'mention') {
      const el = new Y.XmlElement('mention');
      if (node.attrs) {
        for (const [key, value] of Object.entries(
          node.attrs as Record<string, unknown>
        )) {
          if (value !== null && value !== undefined) {
            el.setAttribute(key, JSON.stringify(value));
          }
        }
      }
      children.push(el);
    } else if (node.type === 'hardBreak') {
      children.push(new Y.XmlElement('hardBreak'));
    } else if (
      node.type === 'status' ||
      node.type === 'date' ||
      node.type === 'inlineCard'
    ) {
      const el = new Y.XmlElement(node.type);
      if (node.attrs) {
        for (const [key, value] of Object.entries(
          node.attrs as Record<string, unknown>
        )) {
          if (value !== null && value !== undefined) {
            el.setAttribute(key, JSON.stringify(value));
          }
        }
      }
      children.push(el);
    }
  }
  return children as Y.XmlText[] | Y.XmlElement[];
}

function adfBlockNodeToXmlElement(blockNode: Record<string, any>): Y.XmlElement {
  const el = new Y.XmlElement(blockNode.type);

  if ('attrs' in blockNode && blockNode.attrs) {
    for (const [key, value] of Object.entries(
      blockNode.attrs as Record<string, unknown>
    )) {
      if (value !== null && value !== undefined) {
        el.setAttribute(key, JSON.stringify(value));
      }
    }
  }

  const content = blockNode.content;
  if (!content || !Array.isArray(content)) {
    return el;
  }

  const firstChild = content[0];
  if (firstChild && isBlockNode(firstChild)) {
    for (const child of content as Record<string, any>[]) {
      el.push([adfBlockNodeToXmlElement(child)]);
    }
  } else {
    const children = adfInlineContentToXmlChildren(content as Record<string, any>[]);
    for (const c of children) {
      el.push([c]);
    }
  }

  return el;
}

function xmlInlineChildrenToAdf(
  children: (Y.XmlText | Y.XmlElement)[]
): Record<string, any>[] {
  const content: Record<string, any>[] = [];
  for (const child of children) {
    if (child instanceof Y.XmlText) {
      const delta = child.toDelta();
      for (const d of delta as {
        insert: string;
        attributes?: Record<string, Record<string, unknown>>;
      }[]) {
        const textNode: Record<string, any> = { type: 'text', text: d.insert };
        if (d.attributes && Object.keys(d.attributes).length > 0) {
          textNode.marks = Object.entries(d.attributes).map(([type, markAttrs]) => ({
            type,
            ...(markAttrs && Object.keys(markAttrs).length > 0
              ? { attrs: markAttrs }
              : {})
          }));
        }
        content.push(textNode);
      }
    } else if (child instanceof Y.XmlElement) {
      const node: Record<string, any> = { type: child.nodeName };
      const attrs = child.getAttributes();
      if (Object.keys(attrs).length > 0) {
        node.attrs = {};
        for (const [key, value] of Object.entries(attrs)) {
          try {
            node.attrs[key] = JSON.parse(value as string);
          } catch {
            node.attrs[key] = value;
          }
        }
      }
      if (child.length > 0) {
        const xmlChildren: (Y.XmlText | Y.XmlElement)[] = [];
        for (let j = 0; j < child.length; j++) {
          const c = child.get(j);
          if (c instanceof Y.XmlText || c instanceof Y.XmlElement) {
            xmlChildren.push(c);
          }
        }
        node.content = xmlInlineChildrenToAdf(xmlChildren);
      }
      content.push(node);
    }
  }
  return content;
}

function xmlElementToAdfNode(el: Y.XmlElement): Record<string, any> {
  const node: Record<string, any> = { type: el.nodeName };
  const attrs = el.getAttributes();
  if (Object.keys(attrs).length > 0) {
    node.attrs = {};
    for (const [key, value] of Object.entries(attrs)) {
      try {
        node.attrs[key] = JSON.parse(value as string);
      } catch {
        node.attrs[key] = value;
      }
    }
  }
  if (el.length === 0 && BLOCK_NODE_TYPES.has(el.nodeName)) {
    node.content = [];
  } else if (el.length > 0) {
    const firstChild = el.get(0);
    if (firstChild instanceof Y.XmlElement && BLOCK_NODE_TYPES.has(firstChild.nodeName)) {
      node.content = [];
      for (let j = 0; j < el.length; j++) {
        const c = el.get(j);
        if (c instanceof Y.XmlElement) {
          node.content.push(xmlElementToAdfNode(c));
        }
      }
    } else {
      const xmlChildren: (Y.XmlText | Y.XmlElement)[] = [];
      for (let j = 0; j < el.length; j++) {
        const c = el.get(j);
        if (c instanceof Y.XmlText || c instanceof Y.XmlElement) {
          xmlChildren.push(c);
        }
      }
      node.content = xmlInlineChildrenToAdf(xmlChildren);
    }
  }
  return node;
}
